import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api, unwrap } from '../../../api/client';
import { errorMessage } from '../../../api/errors';
import { useAuth } from '../../../auth/AuthProvider';
import { Badge, Button, Card, Field, Select } from '../../../design';
import { useToast } from '../../../design/Toast';
import { KioskPreview } from '../../kiosk-layouts/KioskPreview';
import type { KioskDoc } from '../../kiosk-layouts/types';
import type { LockerDetail } from '../common';

export function KioskProfileTab({ locker: l }: { locker: LockerDetail }) {
  const { has } = useAuth();
  const canEdit = has('configure');
  const qc = useQueryClient();
  const toast = useToast();
  const layouts = useQuery({
    queryKey: ['kiosk-layouts', { orgId: l.orgId, status: 'published', pageSize: 100 }],
    queryFn: () =>
      unwrap(
        api.GET('/kiosk-layouts', {
          params: { query: { orgId: l.orgId, status: 'published', pageSize: 100 } },
        }),
      ),
  });
  const current = useQuery({
    queryKey: ['kiosk-layout', l.kioskLayoutId],
    queryFn: () =>
      unwrap(api.GET('/kiosk-layouts/{id}', { params: { path: { id: l.kioskLayoutId } } })),
  });
  const [choice, setChoice] = useState(l.kioskLayoutId);
  const [lang, setLang] = useState<string | undefined>(undefined);
  const assign = useMutation({
    mutationFn: (layoutId: string) =>
      unwrap(
        api.PUT('/lockers/{id}/kiosk-layout', {
          params: { path: { id: l.id } },
          body: { layoutId },
        }),
      ),
    onSuccess: () => {
      toast('Layoutul a fost asignat; publică configurația ca să ajungă pe kiosk.', 'success');
      void qc.invalidateQueries({ queryKey: ['locker', l.id] });
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });
  const doc = current.data?.document as unknown as KioskDoc | undefined;
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: '1fr 440px', gap: 16, alignItems: 'start' }}
    >
      <Card>
        <h2>Profil kiosk</h2>
        <p className="muted small" style={{ marginBottom: 12 }}>
          Layoutul de ecran folosit de acest locker. Textele, tema și acțiunile se editează în
          modulul Layouturi kiosk; aici alegi ce layout publicat rulează pe echipament.
        </p>
        <div className="stack">
          <Field label="Layout" htmlFor="kp-layout">
            <Select
              id="kp-layout"
              value={choice}
              disabled={!canEdit}
              onChange={(e) => setChoice(e.target.value)}
            >
              {layouts.data?.items.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name} · v{m.version}
                </option>
              ))}
              {layouts.data &&
                !layouts.data.items.some((m) => m.id === l.kioskLayoutId) &&
                current.data && (
                  <option value={current.data.id}>
                    {current.data.name} · v{current.data.version} ({current.data.status})
                  </option>
                )}
            </Select>
          </Field>
          {canEdit && (
            <div className="flex">
              <Button
                variant="primary"
                onClick={() => assign.mutate(choice)}
                busy={assign.isPending}
                disabled={choice === l.kioskLayoutId}
              >
                Asignează
              </Button>
              <Link to={`/layouturi-kiosk/${choice}`}>
                <Button>Deschide în editor</Button>
              </Link>
            </div>
          )}
          {current.data && (
            <div className="flex wrap small muted">
              <Badge tone={current.data.status === 'published' ? 'green' : 'orange'}>
                {current.data.status}
              </Badge>
              <span>
                v{current.data.version} · limbi: {doc?.languages.join(', ')} · {doc?.actions.length}{' '}
                acțiuni
              </span>
            </div>
          )}
        </div>
      </Card>
      <Card>
        <div className="flex between" style={{ marginBottom: 12 }}>
          <h2>Pe ecran</h2>
          {doc && doc.languages.length > 1 && (
            <Select
              value={lang ?? doc.defaultLanguage}
              onChange={(e) => setLang(e.target.value)}
              aria-label="Limba"
              style={{ width: 110 }}
            >
              {doc.languages.map((x) => (
                <option key={x} value={x}>
                  {x.toUpperCase()}
                </option>
              ))}
            </Select>
          )}
        </div>
        {doc ? (
          <KioskPreview doc={doc} lang={lang} orientation={l.controller.orientation} />
        ) : (
          <p className="muted small">
            {current.error ? errorMessage(current.error) : 'Se încarcă…'}
          </p>
        )}
      </Card>
    </div>
  );
}
